import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  ISignedDocument,
  ISignedDocumentResponse,
  SignerRole
} from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { SignedDocumentStatus } from "@wingspanhq/signed-documents/dist/lib/interfaces/signedDocument";
import { useDownloadMemberDocument } from "../../../../../../query/files/mutations";
import { useUserId } from "../../../../../../query/hooks/helpers";
import { useSignSignedDocument } from "../../../../../../query/signedDocuments/mutations/useSignSignedDocument";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { selectorIsRequirementReadyToSignAsPayee } from "../../../../selectors/selectorIsRequirementReadyToSignAsPayee";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback
} from "../../../../types";
import { getSignaturePayeeRequirementStatus } from "../helpers/getSignaturePayeeRequirementStatus";
import { getSignatureRequirementName } from "../helpers/getSignatureRequirementName";
import { getSignedDocumentProgressSteps } from "../helpers/getSignedDocumentProgressSteps";
import { SIGNATURE_REQUIREMENT_EVENT } from "../utils";
import { payerSignatureRenderCardDescription } from "./payerSignatureRenderCardDescription";

export const usePayerSignatureRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  ISignedDocument | undefined
> => {
  const userId = useUserId();
  const [signDocument] = useSignSignedDocument();
  const [downloadPDFAsPayee, downloadPDFAsPayeeMeta] =
    useDownloadMemberDocument();

  const handleRequirementDownload = async (
    requirement: IRequirementResponse
  ) => {
    await downloadPDFAsPayee(
      {
        documentId: requirement.dataSourceId!
      },
      {
        onSuccess() {
          callback(SIGNATURE_REQUIREMENT_EVENT.DownloadAsPayee, requirement);
        }
      }
    );
  };

  const handleRequirementSign = async (
    requirement: IRequirementResponse,
    document: ISignedDocumentResponse
  ) => {
    await signDocument(document, {
      onSuccess() {
        callback(SIGNATURE_REQUIREMENT_EVENT.SignAsPayee, requirement);
      }
    });
  };

  const getDownloadAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    signedDocument: ISignedDocumentResponse | undefined
  ) => {
    if (
      requirement?.dataSourceId &&
      (signedDocument?.status === SignedDocumentStatus.Complete ||
        signedDocument?.status === SignedDocumentStatus.Pending)
    ) {
      return async () => {
        await handleRequirementDownload(requirement);
      };
    }

    return undefined;
  };

  const getSignAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    signedDocument: ISignedDocumentResponse | undefined
  ) => {
    if (selectorIsRequirementReadyToSignAsPayee(requirement, signedDocument)) {
      return async () => {
        await handleRequirementSign(requirement, signedDocument!);
      };
    }
  };

  return {
    isActionRequired: (requirement, context, signedDocument) => {
      return selectorIsRequirementReadyToSignAsPayee(
        requirement,
        signedDocument
      );
    },
    requirementStatusAlert: (requirement, context, signedDocument) => {
      if (
        selectorIsRequirementReadyToSignAsPayee(requirement, signedDocument)
      ) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the requirement in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      }
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      signedDocument
    ) => {
      return signedDocument
        ? getSignedDocumentProgressSteps(
            signedDocument.status,
            signedDocument.signatures.some(
              ({ signerRole }) => signerRole === SignerRole.Payer
            )
          )
        : undefined;
    },
    requirementName: getSignatureRequirementName,
    requirementDetails: (requirement, context, signedDocument) => {
      const downloadAction = getDownloadAction(
        requirement,
        context,
        signedDocument
      );

      const signAction = getSignAction(requirement, context, signedDocument);

      return {
        header: {
          label: {
            text: getSignatureRequirementName(
              requirement,
              context,
              signedDocument
            )
          }
        },
        children: payerSignatureRenderCardDescription(
          requirement,
          signedDocument
        ),
        data: [],
        actions:
          downloadAction || signAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Download file",
                    onAsyncClick: downloadAction,
                    kind: "Secondary",
                    visible: !!downloadAction
                  },
                  {
                    label: "Start",
                    onAsyncClick: signAction,
                    kind: "Primary",
                    visible: !!signAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, signedDocument) => {
      const actions: RequirementAction[] = [];

      const downloadAction = getDownloadAction(
        requirement,
        context,
        signedDocument
      );

      const signAction = getSignAction(requirement, context, signedDocument);

      if (signAction) {
        actions.push({
          label: "Sign document",
          onAsyncClick: signAction,
          buttonKind: "Primary",
          icon: "edit-line",
          buttonIcon: null,
          mainAction: true
        });
      }

      if (downloadAction) {
        actions.push({
          onAsyncClick: downloadAction,
          label: "Download file",
          buttonKind: signAction ? undefined : "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, signDocument) => {
      return {
        text: getSignatureRequirementName(requirement, context, signDocument)
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      }),
    statusCell: (requirement, context, signedDocument) => {
      return getSignaturePayeeRequirementStatus(
        requirement,
        context,
        signedDocument
      );
    }
  };
};
